import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  FiGlobe, FiSave, FiPlus, FiTrash2, FiSearch, FiMapPin, 
  FiLink, FiEye, FiRefreshCw, FiImage, FiUpload, FiX,
  FiFileText, FiCheck, FiExternalLink, FiZap, FiEdit2,
  FiPackage, FiSettings, FiTarget
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SEOUnifiedPage() {
  const [activeTab, setActiveTab] = useState('pages');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Products from catalog
  const [products, setProducts] = useState([]);
  
  // SEO Pages
  const [seoPages, setSeoPages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProduct, setFilterProduct] = useState('');
  
  // SEO Cities
  const [seoCities, setSeoCities] = useState([]);
  const [newCity, setNewCity] = useState('');
  
  // SEO Settings (meta tags, branding)
  const [seoData, setSeoData] = useState({
    site_url: '',
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    og_image: '',
    favicon: '',
    logo: '',
    logo_dark: '',
    apple_touch_icon: ''
  });

  // Dialog states
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Generation form
  const [genForm, setGenForm] = useState({
    productId: '',
    productName: '',
    cities: []
  });

  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Load products from catalog
      const productsRes = await fetch(`${API_URL}/api/public/products`);
      if (productsRes.ok) {
        const data = await productsRes.json();
        const productsList = Array.isArray(data) ? data : (data.products || []);
        // Filter active products
        const activeProducts = productsList.filter(p => p.active !== false && p.is_active !== false);
        setProducts(activeProducts);
      }
      
      // Load SEO pages
      const pagesRes = await fetch(`${API_URL}/api/seo-pages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (pagesRes.ok) {
        const pages = await pagesRes.json();
        setSeoPages(pages);
      }
      
      // Load site settings (SEO data + cities)
      const siteRes = await fetch(`${API_URL}/api/site-builder/site`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (siteRes.ok) {
        const siteData = await siteRes.json();
        setSeoData(prev => ({
          ...prev,
          site_url: siteData.site_settings?.site_url || 'https://www.justasmile-events.com',
          seo_title: siteData.seo?.title || siteData.site_name || '',
          seo_description: siteData.seo?.description || '',
          seo_keywords: siteData.seo?.keywords || '',
          og_image: siteData.seo?.og_image || '',
          favicon: siteData.site_settings?.favicon || '',
          logo: siteData.site_settings?.logo || '',
          logo_dark: siteData.site_settings?.logo_dark || '',
          apple_touch_icon: siteData.site_settings?.apple_touch_icon || ''
        }));
        setSeoCities(siteData.site_settings?.seo_cities || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Save SEO settings
  const handleSaveSeoSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/site-builder/site`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          seo: {
            title: seoData.seo_title,
            description: seoData.seo_description,
            keywords: seoData.seo_keywords,
            og_image: seoData.og_image
          },
          site_settings: {
            site_url: seoData.site_url,
            favicon: seoData.favicon,
            logo: seoData.logo,
            logo_dark: seoData.logo_dark,
            apple_touch_icon: seoData.apple_touch_icon,
            seo_cities: seoCities
          }
        })
      });

      if (response.ok) {
        toast.success('Paramètres SEO enregistrés');
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  // File upload handler
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !uploadTarget) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/media/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const url = data.url || data.file_url;
        setSeoData(prev => ({ ...prev, [uploadTarget]: url }));
        toast.success('Image uploadée');
      } else {
        toast.error('Erreur lors de l\'upload');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setUploading(false);
      setUploadTarget(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const triggerUpload = (target) => {
    setUploadTarget(target);
    fileInputRef.current?.click();
  };

  // City management
  const addCity = () => {
    const city = newCity.trim();
    if (city && !seoCities.includes(city)) {
      setSeoCities(prev => [...prev, city]);
      setNewCity('');
    }
  };

  const removeCity = (cityToRemove) => {
    setSeoCities(prev => prev.filter(c => c !== cityToRemove));
  };

  // Generate SEO pages
  const generatePages = async () => {
    if (!genForm.productId || genForm.cities.length === 0) {
      toast.error('Sélectionnez un produit et au moins une ville');
      return;
    }

    setGenerating(true);
    let successCount = 0;
    let errorCount = 0;

    const selectedProduct = products.find(p => p.id === genForm.productId);
    const productName = selectedProduct?.name || 'Service';
    const productSlug = productName.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');

    for (const city of genForm.cities) {
      try {
        const citySlug = city.toLowerCase()
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .replace(/\s+/g, '-');
        
        const slug = `${productSlug}-${citySlug}`;
        const title = `${productName} ${city} - Location pour vos événements`;
        
        // Generate content from template
        const content = generateSeoContent(productName, city);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/seo-pages`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            product_id: genForm.productId,
            product_name: productName,
            city: city,
            slug: slug,
            title: title,
            meta_description: `Location de ${productName} à ${city} pour mariage, anniversaire, entreprise. Just A Smile Events - Prestation professionnelle et sur-mesure.`,
            content: content,
            is_published: false // Par défaut non publié
          })
        });

        if (response.ok) {
          successCount++;
        } else {
          const error = await response.json();
          if (error.detail?.includes('existe déjà')) {
            toast.info(`Page "${productName} ${city}" existe déjà`);
          } else {
            errorCount++;
          }
        }

        // Small delay between requests
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (error) {
        errorCount++;
        console.error(`Error generating page for ${city}:`, error);
      }
    }

    setGenerating(false);
    setGenerateDialogOpen(false);
    setGenForm({ productId: '', productName: '', cities: [] });

    if (successCount > 0) {
      toast.success(`${successCount} page(s) générée(s) avec succès`);
      loadAllData();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} page(s) n'ont pas pu être générées`);
    }
  };

  // Generate SEO content from template
  const generateSeoContent = (productName, city) => {
    return `
<div class="seo-page-content">
  <section class="intro">
    <h2>Location de ${productName} à ${city}</h2>
    <p>
      Vous organisez un événement à <strong>${city}</strong> ? Just A Smile Events vous propose la location de 
      <strong>${productName}</strong> pour immortaliser vos plus beaux moments. Que ce soit pour un mariage, 
      un anniversaire, une soirée d'entreprise ou tout autre événement, notre équipe professionnelle 
      vous accompagne de A à Z.
    </p>
  </section>
  
  <section class="services">
    <h3>Nos services de ${productName} à ${city}</h3>
    <ul>
      <li>Installation et configuration sur place</li>
      <li>Personnel qualifié présent durant l'événement</li>
      <li>Impressions illimitées ou selon formule</li>
      <li>Personnalisation des templates</li>
      <li>Galerie en ligne avec toutes les photos/vidéos</li>
      <li>Clé USB avec l'ensemble des médias</li>
    </ul>
  </section>
  
  <section class="why-us">
    <h3>Pourquoi choisir Just A Smile Events à ${city} ?</h3>
    <p>
      Basés dans les Bouches-du-Rhône, nous intervenons à ${city} et dans toute la région PACA. 
      Notre expérience de plus de 500 événements nous permet de vous garantir une prestation 
      de qualité, avec du matériel professionnel et un service client irréprochable.
    </p>
  </section>
  
  <section class="cta">
    <h3>Réservez votre ${productName} à ${city}</h3>
    <p>
      Contactez-nous dès maintenant pour obtenir un devis gratuit et personnalisé. 
      Nous nous adaptons à vos besoins et à votre budget.
    </p>
  </section>
</div>`;
  };

  // Toggle page publish status
  const togglePublish = async (page) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/seo-pages/${page.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_published: !page.is_published })
      });

      if (response.ok) {
        toast.success(page.is_published ? 'Page dépubliée' : 'Page publiée');
        loadAllData();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Delete page
  const deletePage = async (pageId) => {
    if (!window.confirm('Supprimer cette page SEO ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/seo-pages/${pageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Page supprimée');
        loadAllData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Bulk actions
  const bulkPublish = async (publish) => {
    const selectedPages = filteredPages.filter(p => publish ? !p.is_published : p.is_published);
    if (selectedPages.length === 0) {
      toast.info(`Aucune page à ${publish ? 'publier' : 'dépublier'}`);
      return;
    }

    const token = localStorage.getItem('token');
    let count = 0;
    
    for (const page of selectedPages) {
      try {
        await fetch(`${API_URL}/api/seo-pages/${page.id}`, {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_published: publish })
        });
        count++;
      } catch (error) {
        console.error('Error updating page:', error);
      }
    }

    toast.success(`${count} page(s) ${publish ? 'publiée(s)' : 'dépubliée(s)'}`);
    loadAllData();
  };

  // Filter pages
  const filteredPages = seoPages.filter(page => {
    const matchesSearch = !searchTerm || 
      page.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      page.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      page.product_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProduct = !filterProduct || page.product_id === filterProduct;
    return matchesSearch && matchesProduct;
  });

  // Image upload component
  const ImageUploadBox = ({ label, value, target, size = "normal" }) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div 
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-amber-400 transition-colors ${
          value ? 'border-green-300 bg-green-50' : 'border-slate-300'
        }`}
        onClick={() => triggerUpload(target)}
      >
        {value ? (
          <div className="relative inline-block">
            <img 
              src={value} 
              alt={label}
              className={`mx-auto object-contain ${size === 'small' ? 'h-12' : 'h-24'}`}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSeoData(prev => ({ ...prev, [target]: '' }));
              }}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
            >
              <FiX className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="text-slate-500">
            <FiUpload className="h-8 w-8 mx-auto mb-2" />
            <p className="text-sm">Cliquez pour uploader</p>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="seo-unified-page">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/*,.ico"
        className="hidden"
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">SEO & Référencement</h1>
          <p className="text-slate-500 mt-1">Gérez vos pages locales et optimisez votre visibilité</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={loadAllData}>
            <FiRefreshCw className="mr-2 h-4 w-4" />
            Actualiser
          </Button>
          <Button onClick={() => setGenerateDialogOpen(true)} className="bg-amber-600 hover:bg-amber-700">
            <FiZap className="mr-2 h-4 w-4" />
            Générer des pages
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <FiFileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{seoPages.length}</p>
                <p className="text-xs text-slate-500">Pages totales</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <FiCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {seoPages.filter(p => p.is_published).length}
                </p>
                <p className="text-xs text-slate-500">Publiées</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <FiMapPin className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {new Set(seoPages.map(p => p.city)).size}
                </p>
                <p className="text-xs text-slate-500">Villes couvertes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <FiPackage className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {new Set(seoPages.map(p => p.product_id)).size}
                </p>
                <p className="text-xs text-slate-500">Produits</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="pages" className="gap-2">
            <FiFileText className="h-4 w-4" />
            Pages Locales
          </TabsTrigger>
          <TabsTrigger value="cities" className="gap-2">
            <FiMapPin className="h-4 w-4" />
            Villes ({seoCities.length})
          </TabsTrigger>
          <TabsTrigger value="meta" className="gap-2">
            <FiSearch className="h-4 w-4" />
            Meta Tags
          </TabsTrigger>
          <TabsTrigger value="branding" className="gap-2">
            <FiImage className="h-4 w-4" />
            Branding
          </TabsTrigger>
        </TabsList>

        {/* Pages Tab */}
        <TabsContent value="pages" className="space-y-4 mt-6">
          {/* Filters & Actions */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Rechercher une page..."
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <select
                  className="border rounded-md px-3 py-2 text-sm"
                  value={filterProduct}
                  onChange={(e) => setFilterProduct(e.target.value)}
                >
                  <option value="">Tous les produits</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => bulkPublish(true)}>
                    Tout publier
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => bulkPublish(false)}>
                    Tout dépublier
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pages List */}
          {filteredPages.length === 0 ? (
            <Card className="p-12 text-center">
              <FiGlobe className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucune page SEO</h3>
              <p className="text-slate-500 mb-6">
                Générez des pages pour améliorer votre référencement local
              </p>
              <Button onClick={() => setGenerateDialogOpen(true)}>
                <FiZap className="mr-2 h-4 w-4" /> Générer mes premières pages
              </Button>
            </Card>
          ) : (
            <div className="grid gap-3">
              {filteredPages.map((page) => (
                <Card key={page.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      {/* Status indicator */}
                      <div className={`w-2 h-12 rounded-full ${page.is_published ? 'bg-green-500' : 'bg-slate-300'}`} />
                      
                      {/* Main info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900 truncate">
                            {page.product_name} {page.city}
                          </h3>
                          {page.is_published ? (
                            <Badge className="bg-green-100 text-green-700 text-xs">Publiée</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Brouillon</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <FiMapPin className="h-3 w-3" /> {page.city}
                          </span>
                          <span className="flex items-center gap-1">
                            <FiPackage className="h-3 w-3" /> {page.product_name}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1 truncate">/{page.slug}</p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2 mr-2">
                          <Label htmlFor={`publish-${page.id}`} className="text-xs text-slate-500">
                            {page.is_published ? 'Publiée' : 'Brouillon'}
                          </Label>
                          <Switch
                            id={`publish-${page.id}`}
                            checked={page.is_published}
                            onCheckedChange={() => togglePublish(page)}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/${page.slug}`, '_blank')}
                          title="Voir la page"
                        >
                          <FiExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deletePage(page.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          title="Supprimer"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Info box */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <FiTarget className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Comment ça marche ?</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Les pages <strong>publiées</strong> apparaîtront dans le bandeau défilant du footer 
                    sous la forme "Produit + Ville" (ex: "Photobooth Marseille"). Cela améliore votre 
                    référencement local et aide les clients à vous trouver.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cities Tab */}
        <TabsContent value="cities" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FiMapPin className="h-5 w-5 text-amber-500" />
                Gérer les villes SEO
              </CardTitle>
              <CardDescription>
                Ces villes seront proposées lors de la génération de pages SEO
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add new city */}
              <div className="flex gap-2">
                <Input
                  value={newCity}
                  onChange={(e) => setNewCity(e.target.value)}
                  placeholder="Ajouter une ville..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCity())}
                  className="flex-1"
                />
                <Button onClick={addCity} variant="outline" disabled={!newCity.trim()}>
                  <FiPlus className="h-4 w-4 mr-1" />
                  Ajouter
                </Button>
              </div>

              {/* Cities list */}
              <div className="flex flex-wrap gap-2">
                <AnimatePresence>
                  {seoCities.map((city) => (
                    <motion.div
                      key={city}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="flex items-center gap-1 bg-slate-100 px-3 py-1.5 rounded-full group"
                    >
                      <FiMapPin className="h-3 w-3 text-slate-400" />
                      <span className="text-sm">{city}</span>
                      <button
                        onClick={() => removeCity(city)}
                        className="ml-1 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <FiX className="h-3.5 w-3.5" />
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {seoCities.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <FiMapPin className="h-12 w-12 mx-auto mb-2 opacity-20" />
                  <p>Aucune ville configurée</p>
                </div>
              )}

              <div className="pt-4">
                <Button onClick={handleSaveSeoSettings} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                  <FiSave className="mr-2 h-4 w-4" />
                  {saving ? 'Enregistrement...' : 'Sauvegarder les villes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Meta Tags Tab */}
        <TabsContent value="meta" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Balises Meta (SEO Global)</CardTitle>
              <CardDescription>Ces informations apparaissent dans les résultats Google</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>URL du site (pour l'aperçu et le sitemap)</Label>
                <Input
                  value={seoData.site_url}
                  onChange={(e) => setSeoData({ ...seoData, site_url: e.target.value })}
                  placeholder="https://www.justasmile-events.com"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  URL de production de votre site
                </p>
              </div>

              <div>
                <Label>Titre du site (Title Tag)</Label>
                <Input
                  value={seoData.seo_title}
                  onChange={(e) => setSeoData({ ...seoData, seo_title: e.target.value })}
                  placeholder="Just A Smile Events - Location PhotoBooth & VideoBooth 360"
                  className="mt-1"
                  maxLength={60}
                />
                <p className="text-xs text-slate-500 mt-1">
                  {seoData.seo_title?.length || 0}/60 caractères
                </p>
              </div>

              <div>
                <Label>Description (Meta Description)</Label>
                <Textarea
                  value={seoData.seo_description}
                  onChange={(e) => setSeoData({ ...seoData, seo_description: e.target.value })}
                  placeholder="Location de photobooth, videobooth 360° et livre d'or vidéo..."
                  rows={3}
                  className="mt-1"
                  maxLength={160}
                />
                <p className="text-xs text-slate-500 mt-1">
                  {seoData.seo_description?.length || 0}/160 caractères
                </p>
              </div>

              <div>
                <Label>Mots-clés (Keywords)</Label>
                <Input
                  value={seoData.seo_keywords}
                  onChange={(e) => setSeoData({ ...seoData, seo_keywords: e.target.value })}
                  placeholder="photobooth, videobooth 360, location photomaton, mariage"
                  className="mt-1"
                />
              </div>

              {/* Google Preview */}
              <div className="mt-6 p-4 bg-white border rounded-xl">
                <p className="text-xs text-slate-500 mb-3">Aperçu Google :</p>
                <div className="space-y-1">
                  <p className="text-blue-700 text-lg hover:underline cursor-pointer">
                    {seoData.seo_title || 'Titre de votre site'}
                  </p>
                  <p className="text-green-700 text-sm">{seoData.site_url || 'https://www.justasmile-events.com'}</p>
                  <p className="text-slate-600 text-sm">
                    {seoData.seo_description || 'Description de votre site...'}
                  </p>
                </div>
              </div>

              <div className="pt-4">
                <Button onClick={handleSaveSeoSettings} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                  <FiSave className="mr-2 h-4 w-4" />
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Logos & Favicon</CardTitle>
              <CardDescription>Images de marque affichées sur votre site</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <ImageUploadBox 
                  label="Favicon (32x32)" 
                  value={seoData.favicon} 
                  target="favicon"
                  size="small"
                />
                <ImageUploadBox 
                  label="Logo Principal" 
                  value={seoData.logo} 
                  target="logo"
                />
                <ImageUploadBox 
                  label="Logo Mode Sombre" 
                  value={seoData.logo_dark} 
                  target="logo_dark"
                />
                <ImageUploadBox 
                  label="Apple Touch Icon" 
                  value={seoData.apple_touch_icon} 
                  target="apple_touch_icon"
                  size="small"
                />
              </div>

              <div className="pt-6">
                <Button onClick={handleSaveSeoSettings} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                  <FiSave className="mr-2 h-4 w-4" />
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Generate Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FiZap className="h-5 w-5 text-amber-500" />
              Générer des pages SEO
            </DialogTitle>
            <DialogDescription>
              Sélectionnez un produit et des villes pour créer des pages SEO locales
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Product Selection */}
            <div>
              <Label>Produit *</Label>
              <select
                className="w-full border rounded-md p-2 mt-1"
                value={genForm.productId}
                onChange={(e) => {
                  const product = products.find(p => p.id === e.target.value);
                  setGenForm(prev => ({ 
                    ...prev, 
                    productId: e.target.value,
                    productName: product?.name || ''
                  }));
                }}
              >
                <option value="">Sélectionner un produit</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Cities Selection */}
            <div>
              <Label>Villes cibles *</Label>
              <p className="text-xs text-slate-500 mb-2">
                Sélectionnez les villes pour lesquelles générer des pages
              </p>
              
              {seoCities.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 rounded-lg">
                  <FiMapPin className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-slate-500">Aucune ville configurée</p>
                  <p className="text-sm text-slate-400">
                    Allez dans l'onglet "Villes" pour en ajouter
                  </p>
                </div>
              ) : (
                <>
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-3 space-y-1">
                    {seoCities.map(city => (
                      <label key={city} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={genForm.cities.includes(city)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setGenForm(prev => ({ ...prev, cities: [...prev.cities, city] }));
                            } else {
                              setGenForm(prev => ({ ...prev, cities: prev.cities.filter(c => c !== city) }));
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{city}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setGenForm(prev => ({ ...prev, cities: [...seoCities] }))}
                    >
                      Tout sélectionner
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setGenForm(prev => ({ ...prev, cities: [] }))}
                    >
                      Tout désélectionner
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Summary */}
            {genForm.cities.length > 0 && genForm.productId && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="font-medium text-amber-800">
                  {genForm.cities.length} page(s) sera(ont) générée(s)
                </p>
                <p className="text-sm text-amber-600 mt-1">
                  Produit: {genForm.productName}
                </p>
                <p className="text-xs text-amber-500 mt-2">
                  Les pages seront créées en mode "Brouillon". Vous pourrez les publier ensuite.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={generatePages} 
              disabled={generating || !genForm.productId || genForm.cities.length === 0}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {generating ? (
                <>
                  <FiRefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <FiZap className="mr-2 h-4 w-4" />
                  Générer {genForm.cities.length} page(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
